import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useAPIFetchNoAbort } from "../api/use-api-fetch-no-abort";
import { TranscriptMetadataContentRange } from "../core/historical-transcripts/historical-transcripts-table/types";
import { HistoricalTranscriptSearchParams } from "../types/schemas";
import { TranscriptMetadata } from "../types/types";
import { camelCaseDeep } from "../utils/camel-case-deep";

/**
 * Parses contentRange header into a ui friendly format
 */
function parseContentRange(
  contentRange: string,
): TranscriptMetadataContentRange {
  const data = contentRange.split(" ")[1];
  if (data.startsWith("*/")) {
    return {
      startIndex: 0,
      endIndex: 0,
      totalTranscripts: 0,
      hasNextPage: false,
    };
  }
  const [currentRange, total] = data.split("/");
  const [startIndex, endIndex] = currentRange
    .split("-")
    .map((i) => parseInt(i, 10));
  const max = parseInt(total, 10);
  return {
    startIndex,
    endIndex,
    totalTranscripts: max,
    hasNextPage: endIndex < max - 1,
  };
}

export function useTranscriptsMetadataQuery(options: {
  /* page number of the results to fetch (default 0) */
  page: string;
  /* the amount of transcriptions to return per page (default 10, max 100) */
  page_size: string;
  /* string that should be partially matched against name */
  name: HistoricalTranscriptSearchParams["search"];
  /* property to sort by */
  sort?: HistoricalTranscriptSearchParams["sort"];
  /* the direction to sort (default descending) */
  direction?: HistoricalTranscriptSearchParams["direction"];
}): UseQueryResult<
  {
    json: TranscriptMetadata[];
    contentRange: TranscriptMetadataContentRange;
  },
  Error
> {
  const apiFetchNoAbort = useAPIFetchNoAbort();
  return useQuery({
    queryKey: ["transcripts", options],
    queryFn: async () => {
      const undefinedRemoved = Object.fromEntries(
        Object.entries(options).filter(([, v]) => v !== undefined),
      ) as Record<string, string>;
      const urlParams = new URLSearchParams(undefinedRemoved);
      const response = await apiFetchNoAbort.execute(
        `/api/v2/transcriptions?${urlParams}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch transcripts");
      }
      const json = camelCaseDeep(await response.json()) as TranscriptMetadata[];
      const rawContentRange =
        response.headers.get("Content-Range") ||
        response.headers.get("X-Content-Range");
      if (!rawContentRange) throw new Error("No Content-Range header found");
      const contentRange = parseContentRange(rawContentRange);
      return { json, contentRange };
    },
    refetchInterval: 5000,
  });
}
